import { TOKEN_KEY } from '@/consts/const';
import type { GetRefreshTokenResponse } from '@/types/apiTypes';
import { handleMaintenanceHttpError } from '@/utils/maintenance/handleMaintenanceHttpError';

export class RefreshAccessTokenError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RefreshAccessTokenError';
  }
}

type RefreshAccessTokenConfig = {
  signOut: () => void;
  onTokenRefreshed: (token: string) => void;
};

let config: RefreshAccessTokenConfig | null = null;
let refreshPromise: Promise<string> | null = null;

export function configureRefreshAccessToken(nextConfig: RefreshAccessTokenConfig) {
  config = nextConfig;
}

export async function refreshAccessToken(): Promise<string> {
  if (!config) {
    throw new RefreshAccessTokenError('refreshAccessToken is not configured');
  }

  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      config.signOut();
      throw new RefreshAccessTokenError('No access token');
    }

    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/refresh/`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        cache: 'no-store',
      });

      if (!response.ok) {
        if (response.status === 503) {
          const body: unknown = await response.json();
          if (handleMaintenanceHttpError(response.status, body)) {
            throw new RefreshAccessTokenError('Maintenance mode');
          }
        }

        config.signOut();
        throw new RefreshAccessTokenError('Token refresh failed');
      }

      const responseData: GetRefreshTokenResponse = await response.json();
      const newToken = responseData.data.token;

      localStorage.setItem(TOKEN_KEY, newToken);
      config.onTokenRefreshed(newToken);

      return newToken;
    } catch (error) {
      if (error instanceof RefreshAccessTokenError) {
        throw error;
      }

      console.error(error);
      config.signOut();
      throw new RefreshAccessTokenError('Token refresh failed');
    }
  })().finally(() => {
    refreshPromise = null;
  });

  return refreshPromise;
}